// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import WidgetTitle, { WidgetTitleStyles, widgetTitleStyles } from "@bgs/App/Components/WidgetTitle";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    viewAllLink = "viewAllLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.viewAllLink]: LinkFieldValue;
    };
}

export interface LookBookStyles {
    fullWidthWrapper?: InjectableCss;
    fullWidthContent?: InjectableCss;
    titleStyles?: WidgetTitleStyles;
}

export const lookBookStyles: LookBookStyles = {
    fullWidthWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            width: calc(100vw - 9px);
            height: 678px;
            position: relative;
            left: 50%;
            right: 50%;
            margin-left: -50vw;
            margin-right: -50vw;
            @media (max-width: 1440px) {
                left: -40px;
                right: -40px;
                margin-left: 0px;
                margin-right: 0px;
            }

            @media (max-width: 1024px) {
                width: 100vw;
                left: -20px;
                right: -20px;
            }
            @media (max-width: 768px) {
                left: -10px;
                right: -10px;
            }
        `,
    },

    fullWidthContent: {
        css: css`
            display: flex;
            justify-content: center;
            width: 100%;

            [class*="ContentWrapper"] {
                &,
                > div {
                    height: 678px !important;
                }
            }
        `,
    },
    titleStyles: {
        ...widgetTitleStyles,
        wrapper: {
            css: css`
                ${widgetTitleStyles.wrapper?.css}
                flex-direction: column;
            `,
        },
        iconWrapper: {
            css: css`
                ${widgetTitleStyles.iconWrapper?.css}
                position: relative;
                top: 17px;
                left: -21px;
                width: 260px;
            `,
        },
    },
};
const styles = lookBookStyles;

const LookBook = ({ fields, id }: OwnProps) => {
    const {
        title,
        viewAllLink: { value: viewAllUrl },
    } = fields;
    return (
        <>
            <WidgetTitle title={title} viewAllLink={viewAllUrl} icon="LookBook" extendedStyles={styles.titleStyles} />
            <StyledWrapper {...styles.fullWidthWrapper}>
                <StyledWrapper {...styles.fullWidthContent}>
                    <Zone zoneName="Content0" contentId={id} />
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: LookBook,
    definition: {
        group: "Basic",
        icon: "PageTitle",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.viewAllLink,
                displayName: "View All Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
